import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { getCurrentUser } from 'Src/ng2/store';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { PORTAL_TYPES, TPortalLocation } from 'Src/ng2/shared/typings/interfaces/portal.interface';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { UrlNavigationService } from '../../services/url-navigation/url-navigation.service';
import { getPluralLabel } from '../../services/picker/picker.text.service';
import { ENTITY_TYPE, TOpEntityType } from '../../typings/types/entity-type.types';
import { SessionStorageService } from '../../services/web-storage/session-storage/session-storage.service';
import { NvBoxButtonModule } from 'Src/nvps-libraries/design/nv-box-button/nv-box-button.module';
import { ModalsService } from '../../modals/modals.service';
import { NvSharedTooltipModule } from 'projects/shared/nvps-libraries/design/nv-tooltip/nv-shared-tooltip.module';
import { NotesFlyoutPanelService } from '../../flyouts/notes/notes-flyout-panel.service';

@Component({
  selector: 'global-entity-selector',
  templateUrl: './global-entity-selector.component.html',
  styleUrls: ['./global-entity-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [NvBoxButtonModule, NvSharedTooltipModule],
})
export class GlobalEntitySelectorComponent implements OnInit, OnDestroy {
  public currentUser: IUser;
  public selectedView: TPortalLocation;
  public label: string = '';
  public fullLabel: string = '';
  public entityId: string;
  public subEntityId: string | null;
  private entityType: TOpEntityType;
  private networkId: string | null;
  private destroy$ = new Subject<void>();
  public isSwitcherActive: boolean;
  public isPickerOpen: boolean = false;

  constructor (
    private route: ActivatedRoute,
    private store: Store<any>,
    private imUser: ImUser,
    private sessionStorageService: SessionStorageService,
    private modalsService: ModalsService,
    private notesFlyoutPanelService: NotesFlyoutPanelService,
  ) {}

  ngOnInit (): void {
    this.resolveDataInputs();
  }

  ngOnDestroy (): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resolveDataInputs (): void {
    combineLatest([this.store.select(getCurrentUser), this.route.params, this.route.queryParams])
      .pipe(
        takeUntil(this.destroy$),
        filter(([user]) => !!user),
        distinctUntilChanged((prev, curr) => {
          const [, prevParams, prevQueryParams] = prev;
          const [, currParams, currQueryParams] = curr;
          return isEqual(prevParams, currParams) && isEqual(prevQueryParams, currQueryParams);
        }),
        tap(([user]) => {
          this.resolveContext();
          this.currentUser = user;
          this.setInputs(user);
        }),
      )
      .subscribe();
  }

  private setInputs (user: IUser): void {
    const { label, fullLabel } = this.getLabel(user, {
      entityId: this.entityId,
      networkId: this.networkId,
      subEntityId: this.subEntityId,
      entityType: this.entityType,
    });
    this.label = label;
    this.fullLabel = fullLabel;

    const scopesMap = this.currentUser.userScopesEntityTypeMap ?? [];
    const isEntityContextSelectable = this.imUser.isEntityContextSelectable(scopesMap);
    const isMultiDistrictUser = this.sessionStorageService.getItem('districts')?.length > 1;
    this.isSwitcherActive = isMultiDistrictUser || isEntityContextSelectable;
  }

  private getLabel (
    user: IUser,
    context: { entityId: string | null; networkId: string | null; subEntityId: string | null; entityType: TOpEntityType },
  ): { label: string; fullLabel: string } {
    const { entityId, entityType, networkId, subEntityId } = context;
    const scopes = this.imUser.getScopesBasedOnEntityType(user.userScopesEntityTypeMap ?? [], entityType);
    let label: string = '';
    let fullLabel: string = '';
    if (entityId) {
      const currentScope = scopes?.find((scope) => scope.entityId === entityId);
      label = currentScope?.entityNickName || currentScope?.entityName || '';
      fullLabel = currentScope?.entityName || '';
      if (subEntityId) {
        const subEntityName = currentScope?.subEntities?.find((sub) => sub.subEntityId === subEntityId)?.subEntityName;
        label = subEntityName ? `${label} - ${subEntityName}` : label;
        fullLabel = subEntityName ? `${fullLabel} - ${subEntityName}` : fullLabel;
      }
    }
    if (networkId) {
      const currentNetwork = scopes?.flatMap((scope) => scope.networks ?? []).find((network) => network.networkId === networkId);
      label = currentNetwork?.networkName || '';
      fullLabel = label;
    }
    if (!entityId && !networkId) {
      label = `All ${getPluralLabel(entityType)}`;
      fullLabel = label;
    }
    return { label, fullLabel };
  }

  private resolveContext (): void {
    const { entityId, entityType, networkId, subEntityId } = UrlNavigationService.getOpContext(this.route.snapshot);
    this.entityId = entityId;
    this.entityType = entityType;
    this.networkId = networkId;
    this.subEntityId = subEntityId;
    switch (this.entityType) {
      case ENTITY_TYPE.SHELTER:
        this.selectedView = PORTAL_TYPES.SHELTER;
        break;
      case ENTITY_TYPE.SCHOOL:
      default:
        this.selectedView = PORTAL_TYPES.SCHOOL;
        break;
    }
  }

  public openPickerModal (): void {
    const { entityId, entityType, networkId, subEntityId } = UrlNavigationService.getOpContext(this.route.snapshot);
    const subEntityParamName = UrlNavigationService.getSubEntityParamName(entityType);
    this.isPickerOpen = true;
    this.modalsService.openOpPickerModal({
      isInEntityView: true,
      curr: {
        entityType,
        entityId,
        networkId,
        subEntityId,
        subEntityParamName,
      },
    })
      .afterClosed()
      .pipe(
        take(1),
        tap((isNavigating) => {
          this.isPickerOpen = false;
          if (isNavigating) {
            // if navigating away, always close the notes flyout
            this.notesFlyoutPanelService.closeNoteFlyout();
          }
        }),
      )
      .subscribe();
  }
}
